import React, { useState } from 'react'
import { Upload } from 'lucide-react'
import { dexieService } from '../../utils/storage'
import authService from '../../services/authService'
import conflictResolver from '../../utils/conflictResolver'
import ConflictResolutionModal from './ConflictResolutionModal'
import './ImportModal.scss'

const ImportModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [step, setStep] = useState(1)
  const [importData, setImportData] = useState(null)
  const [conflicts, setConflicts] = useState([])
  const [resolutions, setResolutions] = useState([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [showConflictModal, setShowConflictModal] = useState(false)

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result)
        if (!data.products && !data.purchases) {
          alert('Fichier invalide : aucune donnée trouvée')
          return
        }

        setImportData(data)
        setIsAnalyzing(true)

        await analyzeConflicts(data)
      } catch (error) {
        alert('Fichier JSON invalide')
      }
    }
    reader.readAsText(file)
  }

  const analyzeConflicts = async (data) => {
    try {
      const shopId = authService.getShopId()
      if (!shopId) {
        alert('Session invalide. Veuillez vous reconnecter.')
        return
      }

      const allConflicts = await conflictResolver.detectAllConflicts(
        data,
        shopId,
      )

      setConflicts(allConflicts)

      if (allConflicts.length > 0) {
        setStep(2)
        setShowConflictModal(true)
      } else {
        // Aucun conflit, import direct
        await performImport(data, [])
      }
    } catch (error) {
      console.error('Erreur analyse:', error)
      alert("Erreur lors de l'analyse des conflits")
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleConflictResolutions = async (userResolutions) => {
    setShowConflictModal(false)
    setResolutions(userResolutions)
    await performImport(importData, userResolutions)
  }

  const handleCancelConflicts = () => {
    setShowConflictModal(false)
    resetModal()
    onClose()
  }

  const performImport = async (data, userResolutions) => {
    setIsImporting(true)

    try {
      const shopId = authService.getShopId()
      if (!shopId) {
        alert('Session invalide. Veuillez vous reconnecter.')
        return
      }

      const { products = [], purchases = [] } = data

      // Vérifier si l'import contient des données d'une autre boutique
      const hasExternalData =
        products.some((p) => p.shopId && p.shopId !== shopId) ||
        purchases.some((p) => p.shopId && p.shopId !== shopId)

      if (hasExternalData) {
        const confirmImport = window.confirm(
          "ATTENTION : Ce fichier contient des produits ou achats qui n'ont pas été créés par cette boutique.\n\n" +
            'Ces données seront importées et associées à votre boutique actuelle.\n\n' +
            'Voulez-vous continuer ?',
        )

        if (!confirmImport) {
          setIsImporting(false)
          return
        }
      }

      // Appliquer les résolutions utilisateur
      const productResults = await conflictResolver.applyProductResolutions(
        products,
        userResolutions.filter((r) => r.conflict.type === 'product'),
        shopId,
      )

      const purchaseResults = await conflictResolver.applyPurchaseResolutions(
        purchases,
        userResolutions.filter((r) => r.conflict.type === 'purchase'),
        shopId,
      )

      // Import dans la base de données
      let productsAdded = 0
      let productsUpdated = 0
      let purchasesAdded = 0
      let purchasesUpdated = 0

      // CORRECTION : Utilisation de bulkPut au lieu de bulkAdd
      if (productResults.toAdd.length > 0) {
        await dexieService.db.products.bulkPut(productResults.toAdd)
        productsAdded = productResults.toAdd.length
      }

      if (productResults.toUpdate.length > 0) {
        // Utiliser bulkPut pour les mises à jour aussi
        await dexieService.db.products.bulkPut(productResults.toUpdate)
        productsUpdated = productResults.toUpdate.length
      }

      // CORRECTION : Utilisation de bulkPut au lieu de bulkAdd
      if (purchaseResults.toAdd.length > 0) {
        await dexieService.db.purchases.bulkPut(purchaseResults.toAdd)
        purchasesAdded = purchaseResults.toAdd.length
      }

      if (purchaseResults.toUpdate.length > 0) {
        // Utiliser bulkPut pour les mises à jour aussi
        await dexieService.db.purchases.bulkPut(purchaseResults.toUpdate)
        purchasesUpdated = purchaseResults.toUpdate.length
      }

      const message = `Import réussi !\n\nProduits : ${productsAdded} ajoutés, ${productsUpdated} mis à jour\nAchats : ${purchasesAdded} ajoutés, ${purchasesUpdated} mis à jour\n${productResults.toSkip.length + purchaseResults.toSkip.length > 0 ? `\n${productResults.toSkip.length + purchaseResults.toSkip.length} éléments ignorés` : ''}`

      alert(message)
      onImportSuccess()
      resetModal()
      onClose()
    } catch (error) {
      console.error('Erreur import:', error)
      alert("Erreur lors de l'import")
    } finally {
      setIsImporting(false)
    }
  }

  const resetModal = () => {
    setStep(1)
    setImportData(null)
    setConflicts([])
    setResolutions([])
    setShowConflictModal(false)
  }

  if (!isOpen) return null

  return (
    <>
      <div className='modal-overlay'>
        <div className='modal-content lg'>
          <div className='modal-header'>
            <h2 className='modal-title'>Import des données</h2>

            {step === 1 && !isAnalyzing && (
              <div className='upload-section'>
                <input
                  type='file'
                  accept='.json'
                  onChange={handleFileUpload}
                  id='import-file'
                  className='hidden'
                />
                <label htmlFor='import-file' className='upload-label'>
                  <Upload size={48} />
                  <span>Choisir un fichier JSON à importer</span>
                  <small>L'analyse des conflits sera automatique</small>
                </label>
              </div>
            )}

            {isAnalyzing && (
              <div className='analyzing-section'>
                <div className='loading-spinner-big'></div>
                <p>Analyse du fichier et détection des conflits...</p>
              </div>
            )}

            {isImporting && (
              <div className='importing-section'>
                <div className='loading-spinner-big'></div>
                <p>Import en cours...</p>
                <small>Ne fermez pas cette fenêtre</small>
              </div>
            )}

            {!isImporting && !isAnalyzing && (
              <div className='form-row pt-4'>
                <button
                  onClick={() => {
                    onClose()
                    resetModal()
                  }}
                  className='btn secondary flex-1'
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de résolution des conflits */}
      {showConflictModal && (
        <ConflictResolutionModal
          conflicts={conflicts}
          onResolve={handleConflictResolutions}
          onCancel={handleCancelConflicts}
        />
      )}
    </>
  )
}

export default ImportModal
